import React, { Component, ComponentType, ErrorInfo, ReactNode, useCallback, useContext, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { AppContext } from '../window';
import { useTranslation } from '../i18n';
import { Link } from 'react-router';
import { useModal } from '../hooks/useModal';
import { NotificationContext } from '../context/NotificationContext';
import { captureBoundaryError } from '../sentry';
import { lazyWithRetry as ReactLazy } from '../utils/chunkLoad';

type ModalProps = { show: boolean; onHide: () => void };
type ModalModule = Promise<{ default: ComponentType<ModalProps> }>;
const loadTerms = (): ModalModule => import('../about/TermsConditionsModal');
const loadPrivacy = (): ModalModule => import('../about/PrivacyPolicyModal');
const loadContact = (): ModalModule => import('../about/ContactModal');
const loadImpressum = (): ModalModule => import('../about/ImpressumModal');
const loadPrices = (): ModalModule => import('../about/PricesModal');

/**
 * Catches a footer modal that failed (usually its chunk) so it does not reach
 * the root boundary, which would reload or replace the whole page (a paid
 * landing page lost for a footer link). Renders nothing and lets the footer
 * close the modal; no reload, no fallback.
 */
class FooterModalBoundary extends Component<{ children: ReactNode; onError: () => void }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Footer modal failed to load:', error);
    captureBoundaryError(error, errorInfo.componentStack);
    this.props.onError();
  }

  override render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * A lazy footer modal, mounted (and its chunk fetched) from the first time the
 * visitor opens it, then kept mounted so it can animate out. Mounting all of
 * them up front fetched five chunks on every guest page, and one failed fetch
 * blanked the landing page. When it fails, `fail()` closes it, unmounts it and
 * starts from a new `lazy()` (React keeps a rejected one rejected), so the next
 * click fetches the chunk again.
 */
const useLazyModal = (load: () => ModalModule) => {
  const modal = useModal();
  const [Modal, setModal] = useState(() => ReactLazy(load));
  const [opened, setOpened] = useState(false);
  if (modal.isOpen && !opened) {
    setOpened(true);
  }
  const { close } = modal;
  const fail = useCallback(() => {
    close();
    setOpened(false);
    setModal(() => ReactLazy(load));
  }, [close, load]);
  return { ...modal, Modal, mounted: opened || modal.isOpen, fail };
};

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { texts, location, customFrontend } = useContext(AppContext);
  const { addNotification } = useContext(NotificationContext);
  const termsModal = useLazyModal(loadTerms);
  const privacyModal = useLazyModal(loadPrivacy);
  const contactModal = useLazyModal(loadContact);
  const impressumModal = useLazyModal(loadImpressum);
  const pricesModal = useLazyModal(loadPrices);

  const renderModal = (modal: ReturnType<typeof useLazyModal>) => modal.mounted && (
    <FooterModalBoundary
      onError={() => {
        modal.fail();
        addNotification({ level: 'warning', message: t('errors.modal_not_loaded') });
      }}
    >
      <modal.Modal show={modal.isOpen} onHide={modal.close} />
    </FooterModalBoundary>
  );

  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    termsModal.open();
  };

  const handlePrivacyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    privacyModal.open();
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    contactModal.open();
  };

  const handleImpressumClick = (e: React.MouseEvent) => {
    e.preventDefault();
    impressumModal.open();
  };

  const handlePricesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    pricesModal.open();
  };

  // German visitors, and every page of a whitelabel whose custom frontend is German
  // (chatapp.net): German law wants the Impressum reachable from every page of a German site
  const showImpressum = location?.country?.iso?.toLowerCase() === 'de' || customFrontend?.language === 'de';

  return (
    <>
      <footer className="app-footer">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <div className="footer-links">
                <Link to="/about" className="footer-link">
                  {t('general.about_faq')}
                </Link>
                <span className="footer-separator">•</span>
                <a href="#" onClick={handlePricesClick} className="footer-link">
                  {t('about.prices')}
                </a>
                <span className="footer-separator">•</span>
                <a href="#" onClick={handleTermsClick} className="footer-link">
                  {t('about.terms_conditions')}
                </a>
                <span className="footer-separator">•</span>
                <a href="#" onClick={handlePrivacyClick} className="footer-link">
                  {t('about.privacy_policy')}
                </a>
                {showImpressum && (
                  <>
                    <span className="footer-separator">•</span>
                    <a href="#" onClick={handleImpressumClick} className="footer-link">
                      {t('about.impressum')}
                    </a>
                  </>
                )}
                <span className="footer-separator">•</span>
                <a href="#" onClick={handleContactClick} className="footer-link">
                  {t('about.contact')}
                </a>
              </div>
              <div className="footer-copyright">
                <small>
                  {t('general.footer_copyright', { year: "2025 - " +new Date().getFullYear() })}
                  <span className="footer-separator"> • </span>
                  {t('general.footer_disclaimer')}
                </small>
              </div>
            </Col>
          </Row>
        </Container>
      </footer>

      <React.Suspense fallback={null}>
        {renderModal(termsModal)}
        {renderModal(privacyModal)}
        {renderModal(contactModal)}
        {showImpressum && renderModal(impressumModal)}
        {renderModal(pricesModal)}
      </React.Suspense>
    </>
  );
};

export default Footer;
